/*
  One Tooth World — <otw-header> (PRD §6.2, §6.3).
  Sticky shadow after 80px, hide-on-scroll on mobile, mega menu (hover intent 150ms,
  close grace 200ms, keyboard), cart/wishlist count bubbles.
  Shares state with other modules through window.OTW only.
*/
const OTW = (window.OTW = window.OTW || {});
const WISHLIST_KEY = 'otw_wishlist';
const INTENT_DELAY = 150;
const CLOSE_DELAY = 200;
const SHADOW_AFTER = 80;
const mobileQuery = window.matchMedia('(max-width: 749px)');
const desktopQuery = window.matchMedia('(min-width: 990px)');
const hoverQuery = window.matchMedia('(hover: hover)');

function readWishlist() {
  try {
    const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

class OtwHeader extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.hideOnScroll = this.dataset.hideOnScroll === 'true' && this.dataset.sticky === 'true';
    this.lastY = window.scrollY;
    this.ticking = false;
    this.nav = this.querySelector('.otw-nav');
    this.items = Array.from(this.querySelectorAll('.otw-nav__item[data-has-mega]'));
    this.openItem = null;
    this.intentTimer = null;
    this.closeTimer = null;

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', () => this.measure());
    this.measure();
    this.onScroll();

    this.items.forEach((item) => this.bindMega(item));
    this.bindNavKeys();
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.openItem) this.closeMega(true);
    });
    document.addEventListener('click', (event) => {
      if (this.openItem && !this.openItem.contains(event.target)) this.closeMega();
    });

    this.initBubbles();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  /* ---------- Sticky / hide on scroll ---------- */
  measure() {
    const section = this.closest('.shopify-section') || this;
    document.documentElement.style.setProperty('--otw-header-height', `${Math.round(section.offsetHeight)}px`);
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      const y = Math.max(0, window.scrollY);
      this.classList.toggle('is-scrolled', y > SHADOW_AFTER);
      if (this.hideOnScroll && mobileQuery.matches) {
        const delta = y - this.lastY;
        const keepVisible =
          this.contains(document.activeElement) && document.activeElement.matches('input, textarea');
        if (y <= SHADOW_AFTER || delta < -4 || keepVisible) this.classList.remove('is-hidden');
        else if (delta > 4) this.classList.add('is-hidden');
      } else {
        this.classList.remove('is-hidden');
      }
      this.lastY = y;
    });
  }

  /* ---------- Mega menu ---------- */
  bindMega(item) {
    const link = item.querySelector('.otw-nav__link');
    const panel = item.querySelector('.otw-mega');
    if (!link || !panel) return;

    item.addEventListener('mouseenter', () => {
      if (!desktopQuery.matches || !hoverQuery.matches) return;
      clearTimeout(this.closeTimer);
      clearTimeout(this.intentTimer);
      if (this.openItem === item) return;
      // Switching between items while a panel is open is immediate
      const delay = this.openItem ? 0 : INTENT_DELAY;
      this.intentTimer = setTimeout(() => this.openMega(item), delay);
    });

    item.addEventListener('mouseleave', () => {
      clearTimeout(this.intentTimer);
      if (this.openItem !== item) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeMega(), CLOSE_DELAY);
    });

    link.addEventListener('click', (event) => {
      // Touch laptops/tablets: first tap opens the panel, second follows the link
      if (desktopQuery.matches && !hoverQuery.matches && this.openItem !== item) {
        event.preventDefault();
        this.openMega(item);
      }
    });

    link.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (this.openItem === item) this.closeMega(true);
        else this.openMega(item, true);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.openMega(item, true);
      }
    });

    panel.addEventListener('keydown', (event) => this.onPanelKeydown(event, item, link, panel));
    item.addEventListener('focusout', (event) => {
      if (this.openItem === item && !item.contains(event.relatedTarget)) {
        // Focus left the item entirely (tab away); mouse users are handled by mouseleave
        if (event.relatedTarget) this.closeMega();
      }
    });
  }

  openMega(item, focusFirst = false) {
    if (this.openItem && this.openItem !== item) this.closeMega();
    clearTimeout(this.closeTimer);
    const link = item.querySelector('.otw-nav__link');
    const panel = item.querySelector('.otw-mega');
    // Panels are position: fixed (the nav row scrolls horizontally), so anchor them under the nav
    const navBottom = this.nav.getBoundingClientRect().bottom;
    panel.style.setProperty('--otw-mega-top', `${Math.round(navBottom)}px`);
    item.classList.add('is-open');
    link.setAttribute('aria-expanded', 'true');
    this.openItem = item;
    if (focusFirst) {
      const first = panel.querySelector('a');
      if (first) first.focus();
    }
  }

  closeMega(returnFocus = false) {
    clearTimeout(this.intentTimer);
    clearTimeout(this.closeTimer);
    const item = this.openItem;
    if (!item) return;
    const link = item.querySelector('.otw-nav__link');
    item.classList.remove('is-open');
    link.setAttribute('aria-expanded', 'false');
    this.openItem = null;
    if (returnFocus) link.focus();
  }

  onPanelKeydown(event, item, link, panel) {
    const links = Array.from(panel.querySelectorAll('a'));
    const index = links.indexOf(document.activeElement);
    let next = -1;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = index + 1 >= links.length ? 0 : index + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        if (index <= 0) {
          event.preventDefault();
          link.focus();
          return;
        }
        next = index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = links.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    links[next]?.focus();
  }

  bindNavKeys() {
    const links = Array.from(this.querySelectorAll('.otw-nav__link'));
    links.forEach((link, i) => {
      link.addEventListener('keydown', (event) => {
        let target;
        if (event.key === 'ArrowRight') target = links[(i + 1) % links.length];
        else if (event.key === 'ArrowLeft') target = links[(i - 1 + links.length) % links.length];
        if (target) {
          event.preventDefault();
          if (this.openItem) this.closeMega();
          target.focus();
        }
      });
    });
  }

  /* ---------- Count bubbles ---------- */
  initBubbles() {
    const cartBubble = this.querySelector('[data-otw-cart-count]');
    // Seed from the server-rendered value so the first sync doesn't bounce
    OTW.counts = OTW.counts || {};
    if (OTW.counts.cart === undefined) OTW.counts.cart = Number(cartBubble?.textContent) || 0;
    OTW.updateWishlistBubbles = () => OTW.updateBubbles?.('wishlist', readWishlist().length);
    OTW.counts.wishlist = readWishlist().length;
    OTW.updateBubbles?.('wishlist', OTW.counts.wishlist);
    window.addEventListener('storage', (event) => {
      if (event.key === WISHLIST_KEY) OTW.updateWishlistBubbles();
    });
    OTW.on?.('wishlist:change', () => OTW.updateWishlistBubbles());
    OTW.on?.('cart:updated', (detail = {}) => {
      const count = detail.item_count ?? detail.cart?.item_count;
      if (count !== undefined) OTW.updateBubbles?.('cart', count);
    });

    // Cart icon opens the drawer when one exists, else follows the link to /cart
    this.querySelector('[data-otw-cart-trigger]')?.addEventListener('click', (event) => {
      if (!OTW.emit || (!OTW.cart && !document.querySelector('cart-drawer'))) return;
      event.preventDefault();
      OTW.emit('cart:open', { opener: event.currentTarget });
    });
  }
}

if (!customElements.get('otw-header')) customElements.define('otw-header', OtwHeader);

OTW.header = {
  closeMega() {
    document.querySelector('otw-header')?.closeMega?.();
  },
};
